import { DateTime } from 'luxon';
import type { ExpertTipTopicSchedule } from '../types/expert-tip-topic-schedule';

/**
 * Calendar event for react-big-calendar.
 * Each event spans Monday–Sunday to represent one topic per week.
 */
export interface TopicCalendarEvent {
  id: number;
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  resource: {
    scheduleId: number;
    topicId: number;
    topicTitle: string;
    source: 'admin' | 'auto';
    weekStart: string;
  };
}

/**
 * Transform a schedule item into a week-spanning allDay calendar event.
 */
export function transformToTopicCalendarEvent(
  schedule: ExpertTipTopicSchedule
): TopicCalendarEvent {
  const monday = DateTime.fromISO(schedule.weekStart);
  const sunday = monday.plus({ days: 6 }).endOf('day');

  return {
    id: schedule.id,
    title: schedule.source === 'auto'
      ? `Auto: ${schedule.topicTitle}`
      : schedule.topicTitle,
    start: monday.toJSDate(),
    end: sunday.toJSDate(),
    allDay: true,
    resource: {
      scheduleId: schedule.id,
      topicId: schedule.topicId,
      topicTitle: schedule.topicTitle,
      source: schedule.source,
      weekStart: schedule.weekStart,
    },
  };
}

/**
 * Get the Monday of the week containing the given date.
 * Returns ISO string in midnight UTC format for API calls.
 */
export function getWeekStartFromDate(date: Date): string {
  const dt = DateTime.fromJSDate(date);
  const monday = dt.startOf('week'); // Luxon weeks start on Monday (ISO)
  return `${monday.toISODate()}T00:00:00.000Z`;
}

/**
 * Format week label: "Week of Mon, 16 Mar 2026"
 */
export function formatWeekLabel(weekStart: string): string {
  const dt = DateTime.fromISO(weekStart);
  return `Week of ${dt.toFormat('ccc, d LLL yyyy')}`;
}

/**
 * Check if a week's end (Sunday) is today or in the future.
 */
export function isWeekCurrentOrFuture(weekStart: string): boolean {
  const weekDate = DateTime.fromISO(weekStart);
  const today = DateTime.now().setZone('Europe/London').startOf('day');
  const weekEnd = weekDate.plus({ days: 6 });
  return weekEnd >= today;
}

/**
 * Get upcoming unscheduled weeks (next N weeks from current week).
 */
export function getUpcomingWeekStarts(count: number = 4): string[] {
  const now = DateTime.now().setZone('Europe/London');
  const currentMonday = now.startOf('week');

  return Array.from({ length: count }, (_, i) => {
    const monday = currentMonday.plus({ weeks: i });
    return `${monday.toISODate()}T00:00:00.000Z`;
  });
}
